import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useBeaconRouter } from '../route/route.hooks';
import { useActiveWorkspace } from '../store/workspace/workspace.hooks';

export interface RedirectHistoryState {
  redirect?: {
    path: string;
    next?: RedirectHistoryState;
  };
}

const buildRedirectHistoryStateRecursive = (
  paths: string[],
  state: RedirectHistoryState = {},
  index = 0
): RedirectHistoryState | undefined => {
  if (index === paths.length) {
    return;
  }

  return {
    redirect: {
      path: paths[index],
      next: buildRedirectHistoryStateRecursive(paths, state, index + 1),
    },
  };
};

const buildRedirectHistoryState = (
  paths: string[]
): RedirectHistoryState | undefined => {
  return buildRedirectHistoryStateRecursive(paths);
};

type RedirectIfNext = (defaultPath?: string) => void;

const useRedirect = (): RedirectIfNext => {
  const { state } = useLocation<RedirectHistoryState>();
  const { push } = useHistory<RedirectHistoryState>();

  return useCallback(
    (defaultPath?: string) => {
      if (state?.redirect) {
        push(state.redirect.path, state.redirect.next);
      } else if (defaultPath) {
        push(defaultPath);
      }
    },
    [state, push]
  );
};

export type NavigateTo = (options: NavigateToOptions) => Promise<void>;

export interface NavigateToOptions {
  url: string;
}

const useNavigateTo = (): [NavigateTo] => {
  const activeWorkspace = useActiveWorkspace();
  const { push } = useHistory();

  const router = useBeaconRouter();

  const navigateTo: NavigateTo = useCallback(
    async (options: NavigateToOptions): Promise<void> => {
      const { builder, performWindowNavigation } = router;
      const slug = options.url.split('.io').pop();

      if (slug) {
        const parts = slug.split('?');
        let path = parts[0];

        if (path.startsWith('/')) {
          path = path.substring(1);
        }

        const tenantId: string | undefined = path.substring(
          path.startsWith('/') ? 1 : 0,
          path.indexOf('/')
        );

        if (tenantId && tenantId !== '') {
          if (tenantId === activeWorkspace?.tenant.id) {
            push({
              pathname: builder.build(path, {
                prefix: '',
              }),
              search: parts[1],
            });
          } else {
            performWindowNavigation(path);
          }
        } else {
          push({
            pathname: `/${path}`,
            search: parts[1],
          });
        }
      }
    },
    [push, activeWorkspace, router]
  );

  return [navigateTo];
};

export { buildRedirectHistoryState, useRedirect, useNavigateTo };
